"use client";

import React, { useRef, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { motion } from 'framer-motion';
import {
    Globe,
    LayoutGrid,
    Smartphone,
    Palette,
    BadgePercent,
    Camera,
    Search,
} from 'lucide-react';

const containerVariants = {
    hidden: {},
    visible: {
        transition: {
            staggerChildren: 0.1,
        },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, ease: 'easeOut' as const },
    },
};

const SkillsSection = () => {
    const t = useTranslations('Skills');

    const skills = [
        { name: t('websiteDev.title'), level: 95, icon: Globe },
        { name: t('uiUx.title'), level: 85, icon: LayoutGrid },
        { name: t('appDev.title'), level: 90, icon: Smartphone },
        { name: t('graphicDesign.title'), level: 89, icon: Palette },
        { name: t('digitalMarketing.title'), level: 90, icon: BadgePercent },
        { name: t('contentCreation.title'), level: 82, icon: Camera },
        { name: t('seo.title'), level: 78, icon: Search },
    ];

    const expertiseLevelLabel = t('expertiseLevel');

    return (
        <section className="py-16 bg-gray-50 dark:bg-slate-800 lg:py-28">
            <div className="container">
                <div className="max-w-6xl mx-auto">
                    {/* Section Header */}
                    <div className="text-center mb-12">
                        <h2 className="mb-4 text-3xl font-bold">
                            {t('sectionTitle')}
                        </h2>
                        <p className="text-md leading-8 max-w-2xl mx-auto text-muted-foreground">
                            {t('sectionDescription')}
                        </p>
                    </div>

                    {/* Skills Grid */}
                    <motion.div
                        className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8"
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.15 }}
                    >
                        {skills.map((skill, index) => (
                            <SkillCard
                                key={index}
                                name={skill.name}
                                level={skill.level}
                                Icon={skill.icon}
                                expertiseLabel={expertiseLevelLabel}
                            />
                        ))}
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

interface SkillCardProps {
    name: string;
    level: number;
    Icon: React.ComponentType<{ className?: string }>;
    expertiseLabel: string;
}

const SkillCard = ({ name, level, Icon, expertiseLabel }: SkillCardProps) => {
    const barRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.5 }
        );

        if (barRef.current) {
            observer.observe(barRef.current);
        }

        return () => observer.disconnect();
    }, []);

    return (
        <motion.div
            variants={itemVariants}
            className="group p-6 bg-white rounded-lg shadow-lg transition-transform transform hover:-translate-y-2 dark:bg-slate-700"
        >
            {/* Icon + Title */}
            <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold text-md">{name}</h3>
            </div>

            {/* Progress bar */}
            <div ref={barRef}>
                <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-muted-foreground font-medium">
                        {expertiseLabel}
                    </span>
                    <span className="text-xs font-bold text-primary">
                        {level}%
                    </span>
                </div>
                <div className="w-full h-2 bg-gray-200 dark:bg-slate-600 rounded-full overflow-hidden">
                    <div
                        className="h-full rounded-full bg-primary transition-all duration-[1500ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
                        style={{
                            width: isVisible ? `${level}%` : '0%',
                        }}
                    />
                </div>
            </div>
        </motion.div>
    );
};

export default SkillsSection;